import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPencil,
  faTrash,
  faSpinner,
  faSitemap,
  faLock,
  faToggleOn,
  faToggleOff,
} from '@fortawesome/free-solid-svg-icons';
import {
  departmentService,
  type Department,
  type CreateDepartmentPayload,
} from '@/services/department.service';
import { userManagementService } from '@/services/user-management.service';
import { SkeletonPage } from '@/components/skeletons';
import { PageHeader } from '@/components/shared/PageHeader';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { AccessRestricted } from '@/components/shared/AccessRestricted';
import { isForbiddenError } from '@/lib/is-forbidden-error';
import { Pagination } from '@/components/shared/Pagination';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast, confirmDelete } from '@/lib/swal';
import { useDebounce } from '@/hooks/use-debounce';

const PAGE_SIZE = 20;

const DEPT_TYPES = [
  { value: 'GENERAL', label: 'General' },
  { value: 'OPERATIONS', label: 'Operations' },
  { value: 'COMMERCIAL', label: 'Commercial' },
  { value: 'SERVICE', label: 'Service' },
];

function DepartmentForm({
  initial,
  onSubmit,
  onCancel,
  saving,
}: {
  initial?: Partial<CreateDepartmentPayload> & { departmentHeadUserId?: string | null };
  onSubmit: (data: CreateDepartmentPayload & { departmentHeadUserId?: string | null }) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState({
    name: initial?.name ?? '',
    code: initial?.code ?? '',
    description: initial?.description ?? '',
    departmentType: initial?.departmentType ?? 'GENERAL',
    departmentHeadUserId: initial?.departmentHeadUserId ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // pageSize must stay within the endpoint's cap (`user-list-query.dto.ts`:
  // `@Max(100)`). 200 made the request 400, leaving this picker permanently
  // empty with no visible sign that anything had failed.
  const { data: users, isError: usersFailed } = useQuery({
    queryKey: ['users-all'],
    queryFn: () => userManagementService.list({ pageSize: 100, isActive: true }),
  });

  const set =
    (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.code.trim()) errs.code = 'Code is required';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    onSubmit({
      name: form.name,
      code: form.code.toUpperCase(),
      description: form.description || undefined,
      departmentType: form.departmentType,
      departmentHeadUserId: form.departmentHeadUserId || null,
    });
  };

  const inputClass = 'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Department Name" htmlFor="d-name" required error={errors.name}>
          <Input id="d-name" value={form.name} onChange={set('name')} placeholder="Operations" disabled={saving} />
        </FormField>
        <FormField label="Code" htmlFor="d-code" required error={errors.code}>
          <Input id="d-code" value={form.code} onChange={set('code')} placeholder="OPS" disabled={saving} className="uppercase" />
        </FormField>
      </div>
      <FormField label="Department Type" htmlFor="d-type">
        <select id="d-type" value={form.departmentType} onChange={set('departmentType')} disabled={saving} className={inputClass}>
          {DEPT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </FormField>
      <FormField
        label="Department Head"
        htmlFor="d-head"
        error={usersFailed ? 'Could not load the user list. Reload the page to try again.' : undefined}
      >
        <select id="d-head" value={form.departmentHeadUserId ?? ''} onChange={set('departmentHeadUserId')} disabled={saving} className={inputClass}>
          <option value="">— No head assigned —</option>
          {users?.items.map((u) => (
            <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.email})</option>
          ))}
        </select>
      </FormField>
      <FormField label="Description" htmlFor="d-desc">
        <Textarea id="d-desc" value={form.description} onChange={set('description')} rows={3} disabled={saving} />
      </FormField>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="submit" disabled={saving} className="min-w-[100px]">
          {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Save'}
        </Button>
      </div>
    </form>
  );
}

export function DepartmentsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Department | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['departments', page, debouncedSearch],
    queryFn: () => departmentService.list({ page, pageSize: PAGE_SIZE, search: debouncedSearch || undefined }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['departments'] });

  const createMutation = useMutation({
    mutationFn: departmentService.create,
    onSuccess: () => { toast.success('Department created'); setModalOpen(false); invalidate(); },
    onError: () => toast.error('Failed to create department'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Parameters<typeof departmentService.update>[1] }) =>
      departmentService.update(id, payload),
    onSuccess: () => { toast.success('Department updated'); setModalOpen(false); setEditTarget(null); invalidate(); },
    onError: () => toast.error('Failed to update department'),
  });

  const deleteMutation = useMutation({
    mutationFn: departmentService.remove,
    onSuccess: () => { toast.success('Department deleted'); invalidate(); },
    onError: () => toast.error('Cannot delete this department'),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      departmentService.update(id, { isActive }),
    onSuccess: () => { toast.success('Department updated'); invalidate(); },
    onError: () => toast.error('Failed to update department'),
  });

  const handleDelete = async (d: Department) => {
    if (d.isSystem) { toast.error('System departments cannot be deleted'); return; }
    if (await confirmDelete(d.name)) deleteMutation.mutate(d.id);
  };

  const openCreate = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = (d: Department) => { setEditTarget(d); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditTarget(null); };
  const handleSearch = (v: string) => { setSearch(v); setPage(1); };

  const saving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) return <SkeletonPage />;
  // Audit F-2 — a refusal is not an absence. Without this a 403 falls through
  // to the empty state, which reads as a fact about the tenant.
  if (isForbiddenError(error)) return <AccessRestricted />;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Departments"
        description="Manage organizational departments"
        onAdd={openCreate}
        addLabel="Add Department"
      />

      <SearchBar value={search} onChange={handleSearch} placeholder="Search by name or code..." />

      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-foreground">Name</th>
                  <th className="px-4 py-3 font-medium text-foreground">Code</th>
                  <th className="px-4 py-3 font-medium text-foreground">Type</th>
                  <th className="px-4 py-3 font-medium text-foreground">Head</th>
                  <th className="px-4 py-3 font-medium text-foreground">Users</th>
                  <th className="px-4 py-3 font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 font-medium text-foreground sr-only">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((d) => (
                  <tr key={d.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-3 font-medium text-foreground">
                      {d.name}
                      {d.isSystem && (
                        <FontAwesomeIcon icon={faLock} className="ml-2 h-3 w-3 text-muted-foreground" title="System department" />
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{d.code}</td>
                    <td className="px-4 py-3 text-muted-foreground capitalize">{d.departmentType.toLowerCase()}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {d.departmentHead ? `${d.departmentHead.firstName} ${d.departmentHead.lastName}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{d.userCount}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${d.isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {d.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => toggleActiveMutation.mutate({ id: d.id, isActive: !d.isActive })}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          aria-label={d.isActive ? 'Deactivate' : 'Activate'}
                          disabled={toggleActiveMutation.isPending}
                        >
                          <FontAwesomeIcon icon={d.isActive ? faToggleOn : faToggleOff} className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEdit(d)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          aria-label={`Edit ${d.name}`}
                        >
                          <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
                        </button>
                        {!d.isSystem && (
                          <button
                            onClick={() => handleDelete(d)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                            aria-label={`Delete ${d.name}`}
                            disabled={deleteMutation.isPending}
                          >
                            <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />
        </>
      ) : (
        <EmptyState
          icon={faSitemap}
          title={search ? 'No departments match your search' : 'No departments yet'}
          description={search ? 'Try a different search term.' : 'Add your first department to get started.'}
          action={!search ? <Button onClick={openCreate}>Add Department</Button> : undefined}
        />
      )}

      <Modal open={modalOpen} onClose={closeModal} title={editTarget ? 'Edit Department' : 'Add Department'} size="lg">
        <DepartmentForm
          initial={editTarget ? {
            name: editTarget.name,
            code: editTarget.code,
            description: editTarget.description ?? undefined,
            departmentType: editTarget.departmentType,
            departmentHeadUserId: editTarget.departmentHead?.id ?? null,
          } : undefined}
          onSubmit={(payload) =>
            editTarget
              ? updateMutation.mutate({ id: editTarget.id, payload })
              : createMutation.mutate(payload)
          }
          onCancel={closeModal}
          saving={saving}
        />
      </Modal>
    </div>
  );
}
